// /home/heliowallet/frontend/components/FallbackImage.tsx
'use client';

import { useState, useEffect } from 'react';
import { CubeIcon } from '@heroicons/react/24/outline';

interface FallbackImageProps {
  src?: string | null;
  alt: string;
  fallback?: 'chain' | 'token' | 'custom';
  fallbackSrc?: string;
  fallbackText?: string;
  className?: string;
  width?: number;
  height?: number;
}

export default function FallbackImage({
  src,
  alt,
  fallback = 'chain',
  fallbackSrc,
  fallbackText,
  className = '',
  width,
  height
}: FallbackImageProps) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
    setIsLoading(true);
  }, [src]);

  const handleError = () => {
    if (fallbackSrc && !hasError) {
      setImgSrc(fallbackSrc);
      setHasError(true);
    } else {
      setImgSrc(null);
      setHasError(true);
    }
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  // Generate fallback based on type
  const renderFallback = () => {
    if (fallback === 'token') {
      const symbol = fallbackText || alt || '?';
      return (
        <div className={`flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 ${className}`}>
          <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
            {symbol.charAt(0).toUpperCase()}
          </span>
        </div>
      );
    }
    
    if (fallback === 'chain') {
      return (
        <div className={`flex items-center justify-center bg-gradient-to-br from-blue-400 to-purple-500 ${className}`}>
          <CubeIcon className="h-1/2 w-1/2 text-white" />
        </div>
      );
    }
    
    // Custom fallback
    return (
      <div className={`flex items-center justify-center bg-gray-200 dark:bg-gray-700 ${className}`}>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {fallbackText || '?'}
        </span>
      </div>
    );
  };

  if (!imgSrc || hasError) {
    return renderFallback();
  }

  return (
    <>
      {isLoading && (
        <div className={`flex items-center justify-center bg-gray-200 dark:bg-gray-700 animate-pulse ${className}`}>
          <div className="h-full w-full" />
        </div>
      )}
      <img
        src={imgSrc}
        alt={alt}
        className={`${className} ${isLoading ? 'hidden' : ''}`}
        onError={handleError}
        onLoad={handleLoad}
        width={width}
        height={height}
      />
    </>
  );
}